import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { listIndustries, listPhotos } from "../lib/portfolio";
import ShareButton from "../components/portfolio/ShareButton";

const PortfolioIndustry = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [allTags, setAllTags] = useState([]); // [{name, category}, ...] — full catalog
  const [photos, setPhotos] = useState([]);
  const [activeTagsForIndustry, setActiveTagsForIndustry] = useState([]); // string[]
  const [tagFilter, setTagFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  // Resolve industry meta from the public industries endpoint
  useEffect(() => {
    (async () => {
      try {
        const d = await listIndustries();
        const found = (d.items || []).find((i) => i.slug === slug);
        if (!found) {
          toast.error("Industry not found");
          navigate("/portfolio");
          return;
        }
        setIndustry(found);
        setAllTags(d.product_tags || []);
      } catch {
        toast.error("Could not load industry");
      }
    })();
  }, [slug, navigate]);

  // Fetch photos whenever the slug or product-tag filter changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const d = await listPhotos({ industry: slug, product_tag: tagFilter || undefined });
        if (cancelled) return;
        setPhotos(d.items || []);
        // active_product_tags reflects what's actually present in this
        // industry — populate the chip rail when fetching the full list
        // (i.e. when no filter is active).
        if (!tagFilter) setActiveTagsForIndustry(d.active_product_tags || []);
      } catch {
        if (!cancelled) toast.error("Could not load photos");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, tagFilter]);

  // Lightbox navigation handlers — only photo-kind cards are eligible.
  const photoCards = photos.filter((c) => c.kind === "photo");
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const prevLightbox = useCallback(() => {
    setLightboxIdx((i) => (i == null ? null : (i - 1 + photoCards.length) % photoCards.length));
  }, [photoCards.length]);
  const nextLightbox = useCallback(() => {
    setLightboxIdx((i) => (i == null ? null : (i + 1) % photoCards.length));
  }, [photoCards.length]);

  // Keyboard nav inside the lightbox
  useEffect(() => {
    if (lightboxIdx == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevLightbox();
      else if (e.key === "ArrowRight") nextLightbox();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIdx, closeLightbox, prevLightbox, nextLightbox]);

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--ups-paper)" }}>
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--ups-paper)" }}>
      <header
        className="sticky top-0 z-20 border-b"
        style={{ background: "#fff", borderColor: "var(--ups-border)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/portfolio")}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] hover:opacity-70 transition"
            data-testid="portfolio-back-list"
          >
            <ArrowLeft size={14} /> All industries
          </button>
          <ShareButton testid="industry-share-btn" label="Share this industry view" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="text-3xl mb-1">{industry.emoji}</div>
          <h1
            className="font-display text-4xl sm:text-5xl font-black leading-none tracking-tight"
            data-testid="industry-title"
          >
            {industry.label}
          </h1>
          <p className="mt-2 text-[13px] font-mono text-[color:var(--ups-subtext)]">
            {photos.length} {photos.length === 1 ? "photo" : "photos"}{tagFilter ? ` · ${tagFilter}` : ""}
          </p>
        </div>

        {/* Product tag pill rail — grouped by category (per user choice) */}
        {activeTagsForIndustry.length > 0 && (
          <div className="mb-6 space-y-3" data-testid="product-tag-rail">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setTagFilter("")}
                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border-2 transition ${
                  !tagFilter ? "bg-[color:var(--ups-ink)] text-[color:var(--ups-paper)]" : "bg-white hover:bg-[color:var(--ups-muted)]"
                }`}
                style={{ borderColor: "var(--ups-ink)" }}
                data-testid="tag-filter-all"
              >
                All products
              </button>
            </div>
            {(() => {
              // Build a category → [tagName] map filtered to tags that have
              // photos in this industry. Tags missing from allTags fall under
              // "Other".
              const map = {};
              for (const name of activeTagsForIndustry) {
                const meta = allTags.find((t) => t.name === name);
                const cat = meta?.category || "Other";
                (map[cat] = map[cat] || []).push(name);
              }
              return Object.entries(map).map(([cat, names]) => (
                <div key={cat} className="space-y-1.5">
                  <div className="text-[9px] font-bold uppercase tracking-[0.22em] text-[color:var(--ups-subtext)]">{cat}</div>
                  <div className="flex flex-wrap gap-2">
                    {names.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTagFilter(t)}
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm border-2 transition ${
                          tagFilter === t ? "bg-[#FFB500] text-[color:var(--ups-ink)]" : "bg-white hover:bg-[color:var(--ups-muted)]"
                        }`}
                        style={{ borderColor: tagFilter === t ? "#FFB500" : "var(--ups-border)" }}
                        data-testid={`tag-filter-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        )}

        {loading ? (
          <div className="py-20 flex items-center justify-center text-[color:var(--ups-subtext)]">
            <Loader2 className="animate-spin mr-2" size={18} /> Loading…
          </div>
        ) : photos.length === 0 ? (
          <div
            className="py-16 text-center text-sm text-[color:var(--ups-subtext)] border-2 border-dashed rounded-sm"
            style={{ borderColor: "var(--ups-border)" }}
            data-testid="industry-empty"
          >
            {tagFilter
              ? <>No <strong>{tagFilter}</strong> photos in this industry yet — <button onClick={() => setTagFilter("")} className="underline font-bold">show all</button>.</>
              : <>This industry doesn't have published photos yet. Check back soon.</>}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
            {photos.map((card, i) => {
              const isProject = card.kind === "project";
              const onClick = isProject
                ? () => navigate(`/portfolio/project/${card.id}`)
                : () => {
                    const photoIdx = photos.slice(0, i + 1).filter((c) => c.kind === "photo").length - 1;
                    setLightboxIdx(photoIdx);
                  };
              return (
                <button
                  key={`${card.kind}-${card.id}`}
                  onClick={onClick}
                  className="relative group block aspect-square bg-stone-200 overflow-hidden border-2 hover:-translate-y-0.5 transition-transform"
                  style={{ borderColor: "var(--ups-ink)" }}
                  data-testid={isProject ? `project-${card.id}` : `photo-${card.id}`}
                  aria-label={card.name || "Portfolio item"}
                >
                  {card.cover_media_type === "video" ? (
                    <>
                      <video
                        src={card.cover_url}
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)" }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M8 5v14l11-7z"/></svg>
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      src={card.cover_url}
                      alt={card.name || "Print work"}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      referrerPolicy="no-referrer"
                    />
                  )}
                  {isProject && (
                    <>
                      <div className="absolute top-2 left-2 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-[0.14em] flex items-center gap-1"
                           style={{ background: "rgba(0,0,0,0.7)", color: "#FFB500" }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M3 7h6l2 2h10v11H3z"/></svg>
                        {card.media_count}
                      </div>
                      {card.name && (
                        <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 text-[11px] font-bold leading-tight truncate"
                             style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), transparent)", color: "#fff" }}>
                          {card.name}
                        </div>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </main>

      {/* Lightbox — only opens for kind=photo cards */}
      {(() => {
        const photoCards = photos.filter((c) => c.kind === "photo").map((c) => c.photo).filter(Boolean);
        if (lightboxIdx == null || !photoCards[lightboxIdx]) return null;
        const closeLB = (e) => { if (e) e.stopPropagation(); setLightboxIdx(null); };
        const prev = (e) => { e.stopPropagation(); setLightboxIdx((idx) => (idx > 0 ? idx - 1 : photoCards.length - 1)); };
        const next = (e) => { e.stopPropagation(); setLightboxIdx((idx) => (idx < photoCards.length - 1 ? idx + 1 : 0)); };
        const current = photoCards[lightboxIdx];
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.9)" }}
            onClick={closeLightbox}
            data-testid="lightbox"
          >
            <button onClick={closeLB} className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-sm" aria-label="Close" data-testid="lightbox-close"><X size={20} /></button>
            <button onClick={prev} className="absolute left-4 text-white p-2 hover:bg-white/10 rounded-sm" aria-label="Previous"><ChevronLeft size={28} /></button>
            <button onClick={next} className="absolute right-4 text-white p-2 hover:bg-white/10 rounded-sm" aria-label="Next"><ChevronRight size={28} /></button>
            {current.media_type === "video" ? (
              <video src={current.image_url} controls autoPlay playsInline className="max-w-[92vw] max-h-[88vh]" onClick={(e) => e.stopPropagation()} />
            ) : (
              <img src={current.image_url} alt={current.caption || ""} className="max-w-[92vw] max-h-[88vh] object-contain" onClick={(e) => e.stopPropagation()} referrerPolicy="no-referrer" />
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default PortfolioIndustry;
